import axios from 'axios';
import Vue from 'vue';
// eslint-disable-next-line
import * as Sentry from '@sentry/vue';
// eslint-disable-next-line
import store from '@/store';
import { getToken } from '@/services/tokenService';

function isAbortedRequest(error) {
  return axios.isCancel(error) || (error.code === 'ECONNABORTED') || (error.message && error.message.includes('aborted'));
}

const api = axios.create({
  baseURL: 'https://api.checkmotors.net',
  timeout: 25 * 1000
});

const shiftBlockActive = localStorage.getItem('shift_block_active');

let isCheckingAccess = false;
let isRedirectingToLogin = false;

api.interceptors.request.use(async (request) => {
  let token = null;
  if (!request.headers.Authorization) {
    token = await getToken();
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
  }

  const hasToken = token || request.headers.Authorization;

  if (hasToken && shiftBlockActive && !isCheckingAccess) {
    const canAccessSystem = JSON.parse(localStorage.getItem('can_access_system'));

    if (canAccessSystem && canAccessSystem.last_time_checked) {
      const timeChecked = new Date(canAccessSystem.last_time_checked);

      const timeCheckedPlus30Min = new Date(timeChecked.getTime() + 30 * 60000);

      const now = new Date();

      if (now > timeCheckedPlus30Min) {
        isCheckingAccess = true;
        try {
          const response = await api.get('/web/users/can-access-system');
          store.dispatch('Auth/updateUserAccess', response.data);
        } catch (error) {
          console.error('Error checking access system:', error);
        } finally {
          isCheckingAccess = false;
        }
      }
    }
  }

  return request;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAbortedRequest(error)) {
      return Promise.reject(error);
    }

    Sentry.withScope((scope) => {
      if (error.config) {
        scope.setContext('request', {
          url: error.config.url,
          method: error.config.method,
          baseURL: error.config.baseURL,
          params: error.config.params,
          data: error.config.data
        });
      }

      if (error.response) {
        scope.setContext('response', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        scope.setTag('http.status_code', error.response.status);
      } else {
        scope.setTag('error.type', 'network_error');
      }

      if (error.response && error.response.status >= 500) {
        scope.setLevel('error');
      } else if (error.response && error.response.status >= 400) {
        scope.setLevel('warning');
      }

      Sentry.captureException(error);
    });

    if (!error.response) {
      console.error('Network error:', error);
      return Promise.reject(error);
    }

    const message = error.response.data;

    if (message && message.errors !== undefined) {
      message.errors.forEach((e) => {
        if (
          e.msg !== "Cannot read property 'toUpperCase' of undefined" &&
          !e.msg.includes('Cannot read properties of undefined') &&
          !e.msg.includes("Cannot read properties of null (reading 'toUpperCase')")
        ) {
          Vue.$toast.error(e.msg);
        }
      });
    }

    if (error.response.status === 401) {
      const url = error.config?.url || '';

      const ignoredRoutes = [
        '/auth/check/type'
      ];

      const shouldIgnoreRedirect = ignoredRoutes.some((route) => url.includes(route));

      if (!shouldIgnoreRedirect && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        await store.dispatch('Auth/logout');
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
